// LeadsAIAlertsPanel — Section 5 of the new Leads Command Center spec.
//
// One panel that surfaces four categories of issues the AI has
// detected without the operator having to hunt through charts:
//
//   Critical    — will cost money if ignored (never-contacted leads,
//                 unconfirmed appointments)
//   Warning     — data-quality gaps (missing consent, missing DOB)
//   Opportunity — money on the table (leads ready to close, dual
//                 eligible without LIS)
//   Data issue  — contradictions (closed but no premium, appointment
//                 but no scheduled time)
//
// Derived client-side from the leads array + calls + outreach so
// there's no round-trip cost. Rendered on the Today view above the
// priority queue.

import React from 'react';

const DAY_MS = 86_400_000;

function classify(leads, calls, outreach) {
  const now = Date.now();
  const alerts = { critical: [], warning: [], opportunity: [], data_issue: [] };

  const callsBy = {};
  for (const c of calls || []) if (c.leadId) (callsBy[c.leadId] = callsBy[c.leadId] || []).push(c);
  const outrBy = {};
  for (const o of outreach || []) if (o.leadId) (outrBy[o.leadId] = outrBy[o.leadId] || []).push(o);

  for (const l of leads || []) {
    const status = String(l.status || 'new').toLowerCase();
    const activityCount = (callsBy[l.id]?.length || 0) + (outrBy[l.id]?.length || 0);
    const daysOld = (now - Date.parse(l.createdAt || 0)) / DAY_MS;

    // ─── Critical ───────────────────────────────────────────
    if (status === 'new' && activityCount === 0 && daysOld > 1) {
      alerts.critical.push({
        id: `nc-${l.id}`,
        text: `${l.name || 'Unnamed'} — no attempted contact yet (${Math.floor(daysOld)}d old)`,
        action: 'Call now',
        leadId: l.id,
      });
    }
    if (status === 'appt_set' && !l.appointmentConfirmedAt) {
      alerts.critical.push({
        id: `appt-${l.id}`,
        text: `${l.name || 'Unnamed'} — appointment set but not confirmed`,
        action: 'Send confirmation',
        leadId: l.id,
      });
    }

    // ─── Warning (data quality) ────────────────────────────
    if (!l.tcpaConsent && !l.trustedFormCert && activityCount > 0) {
      alerts.warning.push({
        id: `consent-${l.id}`,
        text: `${l.name || 'Unnamed'} — outreach without consent on file`,
        action: 'Review consent',
        leadId: l.id,
      });
    }
    if (activityCount > 0 && !l.phone && !l.email) {
      alerts.warning.push({
        id: `nocontact-${l.id}`,
        text: `${l.name || 'Unnamed'} — activity logged but no phone/email`,
        action: 'Add contact info',
        leadId: l.id,
      });
    }

    // ─── Opportunity ───────────────────────────────────────
    if (status === 'quoted' && daysOld <= 2) {
      alerts.opportunity.push({
        id: `hot-${l.id}`,
        text: `${l.name || 'Unnamed'} — quoted ${Math.floor(daysOld)}d ago, ready to close`,
        action: 'Follow up',
        leadId: l.id,
      });
    }
    if (status === 'sold' && !l.rewardSent) {
      alerts.opportunity.push({
        id: `ref-${l.id}`,
        text: `${l.name || 'Unnamed'} — recent close, ask for referral`,
        action: 'Ask for referral',
        leadId: l.id,
      });
    }

    // ─── Data issues (Harrison's contradictions) ───────────
    if ((status === 'sold' || status === 'closed') && !l.premium) {
      alerts.data_issue.push({
        id: `nopremium-${l.id}`,
        text: `${l.name || 'Unnamed'} — marked closed but no premium recorded`,
        action: 'Add policy details',
        leadId: l.id,
      });
    }
    // "Never contacted" contradiction: has call attempts but still
    // marked 'new'. That's a stage-classification issue.
    if (status === 'new' && (callsBy[l.id]?.length || 0) > 0) {
      alerts.data_issue.push({
        id: `stalestatus-${l.id}`,
        text: `${l.name || 'Unnamed'} — has ${callsBy[l.id].length} call attempt${callsBy[l.id].length === 1 ? '' : 's'} but status is still "New"`,
        action: 'Advance stage',
        leadId: l.id,
      });
    }
  }
  return alerts;
}

const SECTION_STYLE = {
  critical:    { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: '🚨 Critical',    accent: '#991b1b' },
  warning:     { color: '#d97706', bg: '#fef3c7', border: '#fcd34d', label: '⚠️ Warning',     accent: '#92400e' },
  opportunity: { color: '#059669', bg: '#dcfce7', border: '#86efac', label: '💰 Opportunity', accent: '#166534' },
  data_issue:  { color: '#0891b2', bg: '#cffafe', border: '#67e8f9', label: '🔎 Data issue',  accent: '#155e75' },
};

function AlertCategory({ kind, items, onOpen, capacity = 5 }) {
  const s = SECTION_STYLE[kind];
  if (items.length === 0) return null;
  const shown = items.slice(0, capacity);
  const overflow = items.length - shown.length;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 10, padding: 12,
    }}>
      <div style={{
        fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: s.accent, fontWeight: 800, marginBottom: 8,
      }}>{s.label} · {items.length}</div>
      <div style={{ display: 'grid', gap: 4 }}>
        {shown.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#ffffff', border: `1px solid ${s.border}`,
            borderRadius: 6, padding: '6px 10px', fontSize: 12,
          }}>
            <span style={{ flex: 1, color: '#0f172a' }}>{a.text}</span>
            <button
              onClick={() => onOpen?.(a.leadId)}
              style={{
                background: s.color, color: '#ffffff', border: 'none',
                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer',
              }}
            >{a.action}</button>
          </div>
        ))}
        {overflow > 0 && (
          <div style={{ fontSize: 10, color: s.accent, marginTop: 2, fontStyle: 'italic', paddingLeft: 10 }}>
            +{overflow} more
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeadsAIAlertsPanel({ leads = [], calls = [], outreach = [], onOpenLead }) {
  const alerts = React.useMemo(() => classify(leads, calls, outreach), [leads, calls, outreach]);
  const total = alerts.critical.length + alerts.warning.length + alerts.opportunity.length + alerts.data_issue.length;
  if (total === 0) return null;

  return (
    <div style={{
      background: '#ffffff', border: '1px solid #e2e8f0',
      borderRadius: 12, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{
          fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: '#0891b2', fontWeight: 800,
        }}>🤖 AI Attention needed</div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          {total} item{total === 1 ? '' : 's'} flagged across your book
        </div>
      </div>
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      }}>
        <AlertCategory kind="critical"    items={alerts.critical}    onOpen={onOpenLead}/>
        <AlertCategory kind="warning"     items={alerts.warning}     onOpen={onOpenLead}/>
        <AlertCategory kind="opportunity" items={alerts.opportunity} onOpen={onOpenLead}/>
        <AlertCategory kind="data_issue"  items={alerts.data_issue}  onOpen={onOpenLead}/>
      </div>
    </div>
  );
}
